'use client';
import React, { createElement, Fragment, useEffect, useId, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { autocomplete, AutocompleteOptions } from '@algolia/autocomplete-js';
import { Box } from '@mui/material';
import { useTheme, styled } from '@mui/material';
import { Theme } from '@mui/material';
import { safeDataLayerPush } from '../../utils/safeDataLayerPush';

export interface AutocompleteProps extends Omit<AutocompleteOptions<any>, 'container'> {
  placeholder?: string;
  detachedMediaQuery?: string;
  openOnFocus?: boolean;
  filters?: string[];
  facetFilters?: string[];
  variant?: string;
  instanceId?: string;
}

// Styled component for the panel - replaces useStyles
const getPanelStyles = (theme: Theme, variant?: string) => ({
  'zIndex': 1110,
  'margin': 0,
  'boxShadow': 'transparent',
  'borderRadius': '0px 0px 30px 30px',
  'textDecoration': 'none',
  ...(variant !== 'home' && {
    position: 'absolute',
    top: 'calc(100% - 18px) !important',
    left: '0 !important',
    width: '100% !important'
  }),

  '& .aa-Item': {
    '&:last-child': {
      borderRadius: '0px 0px 24px 24px'
    }
  },

  '& .aa-Item[aria-selected=true]': {
    backgroundColor: theme.palette.midnight?.A06
  }
});

export const Autocomplete = ({
  placeholder,
  filters,
  facetFilters,
  variant,
  instanceId,
  onSubmit,
  getSources,
  components,
  ...restProps
}: AutocompleteProps) => {
  const theme = useTheme();
  // Generate a unique ID for this component instance to prevent DOM conflicts during locale switches
  const reactId = useId();
  const uniqueId = instanceId || reactId.replace(/:/g, '');

  // Use refs for callbacks to prevent effect re-runs when parent re-renders
  // This is critical to prevent the autocomplete instance from being destroyed mid-typing
  const onSubmitRef = useRef(onSubmit);
  const getSourcesRef = useRef(getSources);
  const componentsRef = useRef(components);

  // Keep refs up to date with latest values
  onSubmitRef.current = onSubmit;
  getSourcesRef.current = getSources;
  componentsRef.current = components;

  useEffect(() => {
    // Create a style element for the panel classes
    const styleId = `autocomplete-panel-styles-${variant || 'default'}`;
    let styleElement = document.getElementById(styleId) as HTMLStyleElement;

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Generate CSS from the styles object
    const panelStyles = getPanelStyles(theme, variant);
    const className = `aa-panel-${variant || 'default'}`;

    // Convert JS object to CSS string
    const cssString = Object.entries(panelStyles)
      .map(([key, value]) => {
        if (typeof value === 'object') {
          // Handle nested selectors
          const nestedSelector = key;
          const nestedRules = Object.entries(value)
            .map(([nestedKey, nestedValue]) => {
              if (typeof nestedValue === 'object') {
                // Handle double-nested selectors
                const doubleNestedSelector = nestedKey;
                const doubleNestedRules = Object.entries(nestedValue)
                  .map(([k, v]) => `${k.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${v};`)
                  .join('\n  ');
                return `.${className} ${nestedSelector} ${doubleNestedSelector} {\n  ${doubleNestedRules}\n}`;
              }
              return `${nestedKey.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${nestedValue};`;
            })
            .join('\n  ');
          return `.${className} ${nestedSelector} {\n  ${nestedRules}\n}`;
        }
        return `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value};`;
      })
      .join('\n');

    styleElement.textContent = `.${className} {\n${cssString}\n}`;

    // Cache React roots to avoid creating multiple roots for the same container
    const rootsMap = new Map<Element, any>();

    // Use unique selectors for this instance to prevent conflicts during locale switches
    const containerId = `autocomplete-${uniqueId}`;
    const panelContainerId = `autocomplete-container-${uniqueId}`;

    const search = autocomplete({
      ...restProps,
      placeholder,
      getSources: getSourcesRef.current,
      components: componentsRef.current,
      detachedMediaQuery: 'none',
      openOnFocus: false,
      container: `#${containerId}`,
      panelContainer: variant !== 'home' ? `#${panelContainerId}` : undefined,
      panelPlacement: 'input-wrapper-width',
      classNames: { panel: className },
      renderer: { createElement, Fragment },

      render({ children }, root) {
        let reactRoot = rootsMap.get(root);
        if (!reactRoot) {
          reactRoot = createRoot(root);
          rootsMap.set(root, reactRoot);
        }
        reactRoot.render(children as any);
      },
      onSubmit(event) {
        if (event?.state?.query && event?.state?.query !== '') {
          const urlPath = global.location?.pathname;
          safeDataLayerPush({
            event: 'Algolia Search Button Click',
            isCustomEvent: 1,
            url: urlPath,
            query: event.state.query
          });
        }
        // Use ref to get the latest onSubmit callback
        if (onSubmitRef.current) {
          return onSubmitRef.current(event);
        }
        return true;
      }
    });

    return () => {
      // Synchronously destroy search and clean up React roots to prevent race conditions
      // during rapid locale switches where new instances mount before old ones clean up
      search.destroy();
      rootsMap.forEach((root) => {
        root.unmount();
      });
      rootsMap.clear();
      // Clean up style element
      if (styleElement) {
        styleElement.remove();
      }
    };
    // Note: callbacks (onSubmit, getSources, components) are accessed via refs to prevent
    // unnecessary effect re-runs. Only include values that should trigger autocomplete recreation.
  }, [theme, variant, uniqueId, placeholder, filters, facetFilters]);

  // Use unique IDs for this instance's DOM elements
  const containerId = `autocomplete-${uniqueId}`;
  const panelContainerId = `autocomplete-container-${uniqueId}`;

  return <AutocompleteRoot id={containerId} data-panel-container={panelContainerId} data-testid="Autocomplete" />;
};

const AutocompleteRoot = styled(Box, {
  name: 'Autocomplete',
  slot: 'Root',
  overridesResolver: (_, styles) => [styles.root]
})<{}>(() => ({}));

export default Autocomplete;
